import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { TrackerSection } from '../../shared/TrackerSection';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const REGIONS = {
  national: { label: 'National Average', color: 'rgb(75, 192, 192)' },
  CA: { label: 'California', color: 'rgb(255, 99, 132)' },
  TX: { label: 'Texas', color: 'rgb(54, 162, 235)' },
  MA: { label: 'Massachusetts', color: 'rgb(255, 206, 86)' },
  FL: { label: 'Florida', color: 'rgb(153, 102, 255)' }
};

const TIME_RANGE_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: '6months', label: '6 mo' },
  { value: '3months', label: '3 mo' },
  { value: '1month', label: '1 mo' }
];

export const GasPriceTracker = ({ isDark, onLastUpdatedChange }) => {
  const [processedData, setProcessedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('all');

  const apiUrl = process.env.REACT_APP_API_URL || '/api';

  const fetchData = useCallback(async () => {
    const maxRetries = 3;
    const retryDelay = 1000;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(`${apiUrl}/gas-prices`);
        if (!response.ok) throw new Error(`Server returned ${response.status}`);
        return await response.json();
      } catch (err) {
        if (attempt === maxRetries) throw err;
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
  }, [apiUrl]);

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchData();
        if (cancelled) return;
        const processed = data
          .map(item => ({
            date: new Date(item.date).toLocaleDateString('en-US', { timeZone: 'UTC' }),
            price: parseFloat(item.price),
            region: item.region,
            lastUpdated: item.lastUpdated,
            timestamp: new Date(item.date).getTime()
          }))
          .sort((a, b) => a.timestamp - b.timestamp);
        setProcessedData(processed);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadData();
    return () => { cancelled = true; };
  }, [fetchData]);

  const filteredData = useMemo(() => {
    if (timeRange === 'all') return processedData;
    const cutoffDate = new Date();
    switch (timeRange) {
      case '1month': cutoffDate.setMonth(cutoffDate.getMonth() - 1); break;
      case '3months': cutoffDate.setMonth(cutoffDate.getMonth() - 3); break;
      case '6months': cutoffDate.setMonth(cutoffDate.getMonth() - 6); break;
      default: return processedData;
    }
    return processedData.filter(item => item.timestamp >= cutoffDate.getTime());
  }, [timeRange, processedData]);

  const uniqueDates = useMemo(() =>
    [...new Set(filteredData.map(item => item.date))]
      .sort((a, b) => new Date(a) - new Date(b))
  , [filteredData]);

  const chartData = useMemo(() => ({
    labels: uniqueDates,
    datasets: Object.entries(REGIONS).map(([region, config]) => ({
      label: config.label,
      data: uniqueDates.map(date => {
        const point = filteredData.find(item => item.date === date && item.region === region);
        return point ? point.price : null;
      }),
      borderColor: config.color,
      backgroundColor: config.color,
      tension: 0.1,
      spanGaps: true
    }))
  }), [filteredData, uniqueDates]);

  const chartOptions = useMemo(() => {
    const gridColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
    const tickColor = isDark ? '#c4c4c8' : '#6b7280';
    const legendColor = isDark ? '#c4c4c8' : '#4b5563';
    return {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 2.6,
      interaction: { intersect: false, mode: 'index' },
      animation: { duration: 0 },
      hover: { animationDuration: 0 },
      responsiveAnimationDuration: 0,
      plugins: {
        tooltip: {
          enabled: true,
          animation: false,
          backgroundColor: isDark ? 'rgba(245, 245, 245, 0.95)' : 'rgba(17, 24, 39, 0.95)',
          titleColor: isDark ? '#0b0b0d' : '#f5f5f5',
          bodyColor: isDark ? '#0b0b0d' : '#f5f5f5',
          padding: 12,
          cornerRadius: 6,
          displayColors: true
        },
        legend: {
          position: 'top',
          align: 'center',
          labels: { color: legendColor, padding: 16, boxWidth: 20 }
        },
        title: { display: false }
      },
      scales: {
        y: {
          beginAtZero: false,
          grid: { color: gridColor },
          ticks: {
            color: tickColor,
            callback: (value) => '$' + value.toFixed(2)
          }
        },
        x: {
          grid: { color: gridColor },
          ticks: {
            color: tickColor,
            maxRotation: 45,
            minRotation: 45
          }
        }
      }
    };
  }, [isDark]);

  const stats = useMemo(() => {
    const national = filteredData.filter(d => d.region === 'national' && Number.isFinite(d.price));
    if (!national.length) {
      return [
        { label: 'Current', value: '—' },
        { label: 'Peak', value: '—' },
        { label: 'Change', value: '—' },
        { label: 'Source', value: 'EIA / Monthly' }
      ];
    }
    const prices = national.map(d => d.price);
    const current = prices[prices.length - 1];
    const first = prices[0];
    const peak = Math.max(...prices);
    const pctChange = first ? ((current - first) / first) * 100 : 0;
    const trend = pctChange > 0.05 ? 'up' : pctChange < -0.05 ? 'down' : null;
    return [
      { label: 'Current', value: `$${current.toFixed(2)}` },
      { label: 'Peak', value: `$${peak.toFixed(2)}` },
      {
        label: 'Change',
        value: `${pctChange >= 0 ? '+' : ''}${pctChange.toFixed(1)}%`,
        trend
      },
      { label: 'Source', value: 'EIA / Monthly' }
    ];
  }, [filteredData]);

  useEffect(() => {
    if (processedData.length && onLastUpdatedChange) {
      const latest = processedData[processedData.length - 1];
      onLastUpdatedChange(latest.lastUpdated || latest.date);
    }
  }, [processedData, onLastUpdatedChange]);

  return (
    <TrackerSection
      label="GAS"
      title="U.S. retail gasoline, per gallon"
      timeRange={timeRange}
      onTimeRangeChange={setTimeRange}
      timeRangeOptions={TIME_RANGE_OPTIONS}
      stats={stats}
      source="U.S. Energy Information Administration — monthly retail gasoline prices"
      cadence="Updated monthly"
      loading={loading}
      error={error}
    >
      <Line options={chartOptions} data={chartData} />
    </TrackerSection>
  );
};

export default GasPriceTracker;
